/**
 * VÉRAULT Travels — Nylas Scheduler embed.
 *
 * Wraps the official @nylas/web-elements <nylas-scheduling> custom element
 * so the consultation booking flow stays inside the V&Eacute;RAULT page —
 * no jarring third-party tab-switch, no iframe (Nylas sets
 * `frame-ancestors 'none'`, so iframes are refused).
 *
 * The script is loaded once at first use and cached on window. The
 * component renders a graceful loading state until the custom element
 * registers, then unveils the scheduler.
 */
import React from 'react';
import { NYLAS_HOST_CSS_VARS, NYLAS_HOST_CSS_VARS_DARK, injectIntoNylasShadow } from './NylasBranding';

const SCRIPT_URL =
  'https://cdn.jsdelivr.net/npm/@nylas/web-elements@latest/dist/nylas-web-elements/nylas-web-elements.esm.js';

let scriptPromise = null;

function loadNylasElements() {
  if (typeof window === 'undefined') return Promise.resolve();
  if (window.customElements?.get('nylas-scheduling')) return Promise.resolve();
  if (scriptPromise) return scriptPromise;
  scriptPromise = new Promise((resolve, reject) => {
    const existing = document.querySelector(`script[src="${SCRIPT_URL}"]`);
    if (existing) {
      existing.addEventListener('load', resolve, { once: true });
      existing.addEventListener('error', reject, { once: true });
      return;
    }
    const s = document.createElement('script');
    s.src = SCRIPT_URL;
    s.type = 'module';
    s.async = true;
    s.onload = resolve;
    s.onerror = reject;
    document.head.appendChild(s);
  });
  return scriptPromise;
}

export default function NylasScheduler({
  configurationId,
  slug,
  appId,
  clientId,
  schedulerApiUrl,
  bookingConfirmationRedirectUrl,
  fallbackUrl,
  prefilledName,
  prefilledEmail,
  className = '',
  testid,
  onBooking,
}) {
  const [ready, setReady] = React.useState(false);
  const [failed, setFailed] = React.useState(false);
  const containerRef = React.useRef(null);
  const onBookingRef = React.useRef(onBooking);
  React.useEffect(() => { onBookingRef.current = onBooking; }, [onBooking]);

  React.useEffect(() => {
    let alive = true;
    loadNylasElements()
      .then(() => {
        if (!alive) return;
        // Wait for the custom element to register (script is type=module and may
        // resolve `load` before customElements.define() runs).
        if (window.customElements?.get('nylas-scheduling')) {
          setReady(true);
        } else {
          window.customElements
            .whenDefined('nylas-scheduling')
            .then(() => alive && setReady(true))
            .catch(() => alive && setFailed(true));
        }
      })
      .catch(() => alive && setFailed(true));
    return () => {
      alive = false;
    };
  }, []);

  // Imperatively create the custom element. Two embed paths supported:
  //  - configurationId  → preferred when you have the internal config ID
  //  - slug + appId      → public-config alternative, lifted directly from
  //                        the hosted URL `book.nylas.com/{region}/{appId}/{slug}`
  // The web-elements component accepts either pattern; if both are passed,
  // configurationId wins.
  React.useEffect(() => {
    if (!ready || !containerRef.current) return;
    const host = containerRef.current;
    let el = host.firstElementChild;
    const isNew = !el;
    if (isNew) {
      el = document.createElement('nylas-scheduling');
      if (configurationId) {
        el.setAttribute('configuration-id', configurationId);
      }
      if (slug) el.setAttribute('slug', slug);
      if (appId) el.setAttribute('app-id', appId);
      if (clientId) el.setAttribute('client-id', clientId);
      el.setAttribute('mode', 'app');
      el.setAttribute('scheduler-api-url', schedulerApiUrl || 'https://api.us.nylas.com/v3');
      // After-booking redirect — keeps the visitor inside VÉRAULT instead of
      // stranding them on the Nylas confirmation screen. We forward the
      // current page URL so PlanPage can flip to its branded thank-you state.
      if (bookingConfirmationRedirectUrl) {
        el.setAttribute('nylas-booking-confirmation-redirect-url', bookingConfirmationRedirectUrl);
        el.setAttribute('nylas-booking-confirmation-type', 'external');
      }
      el.style.width = '100%';
      el.style.minHeight = '720px';
      el.style.display = 'block';
    }

    // Wave 30.4.6 — Apply VÉRAULT host vars (light or dark) BEFORE
    // any shadow injection so Nylas' own internal CSS sees our values
    // when it first evaluates. Re-applied on every effect run so a
    // theme toggle is picked up the moment the user flips it.
    const theme = (typeof document !== 'undefined' && document.documentElement.getAttribute('data-theme')) || 'light';
    const hostCss = theme === 'dark' ? NYLAS_HOST_CSS_VARS_DARK : NYLAS_HOST_CSS_VARS;
    // Strip any prior VT host vars before re-applying — keep only
    // the geometric defaults (width / minHeight / display).
    const baseSize = 'width: 100%; min-height: 720px; display: block;';
    el.style.cssText = baseSize + hostCss;
    // Wave 26.14 — pre-fill name + email when the visitor is already
    // authenticated (e.g. inside the client portal). Re-applied even when
    // the element already exists, since profile data may arrive after the
    // element is mounted. Different versions of the web-element use
    // different APIs, so we set both attributes and the modern JS property.
    if (prefilledName || prefilledEmail) {
      if (prefilledName) el.setAttribute('prefilled-name', prefilledName);
      if (prefilledEmail) el.setAttribute('prefilled-email', prefilledEmail);
      try {
        el.prefilledFields = {
          ...(prefilledName ? { name: prefilledName } : {}),
          ...(prefilledEmail ? { email: prefilledEmail } : {}),
        };
        el.bookingInfo = {
          ...(el.bookingInfo || {}),
          primaryParticipant: {
            ...(prefilledName ? { name: prefilledName } : {}),
            ...(prefilledEmail ? { email: prefilledEmail } : {}),
          },
        };
      } catch {
        // Property assignment unsupported on this version — attributes only.
      }
    }

    // Wave 30.4.6 — ALWAYS (re-)run brand injection, even when the
    // element already exists. React StrictMode double-fires effects;
    // the first cleanup tears down the previous shadow style, so we
    // must re-inject on every effect run (cheap — idempotent via a
    // WeakSet inside injectIntoNylasShadow).
    const teardownShadow = injectIntoNylasShadow(el);

    if (!isNew) {
      return () => {
        try { teardownShadow(); } catch { /* noop */ }
      };
    }

    // Hook up booking-confirmed events. Nylas web-elements emit
    // `bookingConfirmed` on the element itself (NOT bubbled to document),
    // but earlier docs referenced `nylas-booking-confirmed`. We listen
    // for both + a few defensive variants so we never miss the event.
    const candidates = [
      'bookingConfirmed',
      'nylas-booking-confirmed',
      'nylasBookingConfirmed',
      'bookingSent',
      'bookingFormSubmitted',
    ];
    const handler = (e) => {
      try {
        onBookingRef.current?.(e?.detail || {}, e?.type);
      } catch (err) {
        // eslint-disable-next-line no-console
        console.warn('[NylasScheduler] onBooking handler threw:', err);
      }
    };
    candidates.forEach((name) => el.addEventListener(name, handler));
    // Also listen on document for legacy bubbled variants.
    candidates.forEach((name) => document.addEventListener(name, handler));
    host.appendChild(el);

    return () => {
      candidates.forEach((name) => el.removeEventListener(name, handler));
      candidates.forEach((name) => document.removeEventListener(name, handler));
      try { teardownShadow(); } catch { /* noop */ }
    };
  }, [ready, configurationId, slug, appId, clientId, schedulerApiUrl, bookingConfirmationRedirectUrl, prefilledName, prefilledEmail]);

  // Wave 30.4.6 — Live theme toggle: when `data-theme` flips on
  // <html>, re-apply the host CSS vars so the Nylas embed swaps
  // light/dark instantly. Decoupled from the main mount effect so we
  // don't tear down event listeners.
  React.useEffect(() => {
    if (!ready) return undefined;
    const host = containerRef.current;
    const apply = () => {
      const el = host?.firstElementChild;
      if (!el) return;
      const theme = document.documentElement.getAttribute('data-theme') || 'light';
      const hostCss = theme === 'dark' ? NYLAS_HOST_CSS_VARS_DARK : NYLAS_HOST_CSS_VARS;
      const baseSize = 'width: 100%; min-height: 720px; display: block;';
      el.style.cssText = baseSize + hostCss;
    };
    apply();
    const observer = new MutationObserver(apply);
    observer.observe(document.documentElement, { attributes: true, attributeFilter: ['data-theme'] });
    return () => observer.disconnect();
  }, [ready]);

  // Old document-only listener removed — the comprehensive listener
  // now lives in the element-creation useEffect above (covers element
  // events + bubbled document events for several variant names).

  if (failed) {
    return (
      <div className={`flex flex-col items-center justify-center py-16 ${className}`} data-testid={`${testid}-failed`}>
        <p className="font-accent italic text-[14.5px] text-[hsl(var(--foreground-secondary))] mb-5 max-w-md text-center">
          The calendar couldn't reach us just now. Please try a fresh window — we'll meet you there.
        </p>
        {fallbackUrl && (
          <a
            href={fallbackUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-[hsl(var(--foreground))] text-[hsl(var(--background))] hover:bg-[hsl(var(--foreground))]/92 text-sm font-medium transition-colors"
          >
            Open in a new tab
          </a>
        )}
      </div>
    );
  }

  return (
    <div className={className} data-testid={testid}>
      {!ready && (
        <div className="flex flex-col items-center justify-center py-24" data-testid={`${testid}-loading`}>
          <span className="block w-1.5 h-1.5 rounded-full bg-[hsl(var(--accent-gold))] animate-pulse mb-5" />
          <p className="font-mono text-[10px] uppercase tracking-[0.28em] text-[hsl(var(--foreground-muted))]">
            Opening the calendar
          </p>
          <p className="mt-3 font-accent italic text-[13px] text-[hsl(var(--foreground-muted))]">
            One quiet moment…
          </p>
        </div>
      )}
      <div ref={containerRef} data-testid={`${testid}-host`} />
    </div>
  );
}
